import { useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import BackToTop from '../../components/feature/BackToTop';
import SolutionsHero from './components/SolutionsHero';
import STEMLab from './components/STEMLab';
import AIRoboticsLab from './components/AIRoboticsLab';
import InnovationWorkshops from './components/InnovationWorkshops';
import TeacherEnablement from './components/TeacherEnablement';
import SafetyCompliance from './components/SafetyCompliance';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

export default function Solutions() {
  useEffect(() => {
    document.title = 'Our Solutions | STEMForge Tech Labs | STEM, AI & Robotics Education';

    const setMeta = (name: string, content: string, attr = 'name') => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', 'Explore STEMForge comprehensive STEM solutions: Lab setup, AI & Robotics programs, Innovation workshops, Career guidance, and Teacher training for schools.');
    setMeta('keywords', 'STEM lab setup, AI robotics education, innovation workshops, teacher training, STEMForge solutions');
    setMeta('robots', 'index, follow');
    setMeta('og:title', 'Our Solutions | STEMForge Tech Labs | STEM, AI & Robotics Education', 'property');
    setMeta('og:description', 'Explore STEMForge comprehensive STEM solutions: Lab setup, AI & Robotics programs, Innovation workshops, Career guidance, and Teacher training for schools.', 'property');
    setMeta('og:url', `${SITE_URL}/solutions`, 'property');
    setMeta('og:type', 'website', 'property');
    setMeta('twitter:title', 'Our Solutions | STEMForge Tech Labs | STEM, AI & Robotics Education');
    setMeta('twitter:description', 'Explore STEMForge comprehensive STEM solutions: Lab setup, AI & Robotics programs, Innovation workshops, Career guidance, and Teacher training for schools.');

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = `${SITE_URL}/solutions`;

    const schemaId = 'schema-solutions';
    let script = document.getElementById(schemaId);
    if (!script) {
      script = document.createElement('script');
      script.id = schemaId;
      (script as HTMLScriptElement).type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${SITE_URL}/solutions`,
      url: `${SITE_URL}/solutions`,
      name: 'Our Solutions | STEMForge Tech Labs',
      description: 'Explore STEMForge comprehensive STEM solutions: Lab setup, AI & Robotics programs, Innovation workshops, Career guidance, and Teacher training for schools.',
      dateModified: '2026-04-10',
      isPartOf: { '@type': 'WebSite', url: SITE_URL, name: 'STEMForge Tech Labs' },
      mainEntity: {
        '@type': 'ItemList',
        name: 'STEMForge Solutions',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            item: {
              '@type': 'Service',
              name: 'STEM Lab Setup',
              description: 'Complete STEM laboratory setup with modern equipment and curriculum alignment.',
              provider: { '@type': 'Organization', name: 'STEMForge Tech Labs' },
            },
          },
          {
            '@type': 'ListItem',
            position: 2,
            item: {
              '@type': 'Service',
              name: 'AI & Robotics Lab',
              description: 'Hands-on AI and Robotics programs for school students.',
              provider: { '@type': 'Organization', name: 'STEMForge Tech Labs' },
            },
          },
          {
            '@type': 'ListItem',
            position: 3,
            item: {
              '@type': 'Service',
              name: 'Innovation Workshops',
              description: 'Creative innovation workshops fostering problem-solving and design thinking.',
              provider: { '@type': 'Organization', name: 'STEMForge Tech Labs' },
            },
          },
          {
            '@type': 'ListItem',
            position: 4,
            item: {
              '@type': 'Service',
              name: 'Teacher Enablement',
              description: 'Comprehensive teacher training programs for STEM education delivery.',
              provider: { '@type': 'Organization', name: 'STEMForge Tech Labs' },
            },
          },
          {
            '@type': 'ListItem',
            position: 5,
            item: {
              '@type': 'Service',
              name: 'Safety & Compliance',
              description: 'Lab safety standards and compliance frameworks for school environments.',
              provider: { '@type': 'Organization', name: 'STEMForge Tech Labs' },
            },
          },
        ],
      },
    });

    return () => {
      const s = document.getElementById(schemaId);
      if (s) s.remove();
    };
  }, []);

  useEffect(() => {
    const sectionId = sessionStorage.getItem('scrollToSection');
    if (sectionId) {
      sessionStorage.removeItem('scrollToSection');
      setTimeout(() => {
        const el = document.getElementById(sectionId);
        if (el) {
          const offset = 80;
          const top = el.getBoundingClientRect().top + window.scrollY - offset;
          window.scrollTo({ top, behavior: 'smooth' });
        }
      }, 100);
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <SolutionsHero />
      <STEMLab />
      <AIRoboticsLab />
      <InnovationWorkshops />
      <TeacherEnablement />
      <SafetyCompliance />
      <Footer />
      <BackToTop />
    </div>
  );
}
